export type DevImpersonationSession = {
  originalToken: string
  originalRefreshToken: string
  targetUserId: string
  targetEmail: string
  startedAt: number
}

export const DEV_IMPERSONATION_SESSION_KEY = 'fusegrid_dev_impersonation_session'
const AUTH_TOKEN_KEY = 'fusegrid_api_token'
const AUTH_REFRESH_TOKEN_KEY = 'fusegrid_api_refresh_token'
const AUTH_STATE_EVENT = 'fusegrid_api_auth_state_changed'
const DEV_IMPERSONATION_EVENT = 'fusegrid_dev_impersonation_changed'

const clean = (value: unknown): string => String(value ?? '').trim()

export function readDevImpersonationSession(): DevImpersonationSession | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = clean(window.sessionStorage.getItem(DEV_IMPERSONATION_SESSION_KEY))
    if (!raw) return null
    const parsed = JSON.parse(raw)
    const session: DevImpersonationSession = {
      originalToken: clean(parsed?.originalToken),
      originalRefreshToken: clean(parsed?.originalRefreshToken),
      targetUserId: clean(parsed?.targetUserId),
      targetEmail: clean(parsed?.targetEmail),
      startedAt: Number(parsed?.startedAt || 0),
    }
    if (!session.originalToken || !session.originalRefreshToken || !session.targetUserId) return null
    return session
  } catch {
    return null
  }
}

export function startDevImpersonationSession(session: DevImpersonationSession): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(DEV_IMPERSONATION_SESSION_KEY, JSON.stringify({
      originalToken: clean(session.originalToken),
      originalRefreshToken: clean(session.originalRefreshToken),
      targetUserId: clean(session.targetUserId),
      targetEmail: clean(session.targetEmail),
      startedAt: Number.isFinite(session.startedAt) && session.startedAt > 0 ? session.startedAt : Date.now(),
    }))
    window.dispatchEvent(new Event(DEV_IMPERSONATION_EVENT))
  } catch {
    // Session restore is a local-dev convenience; token switching already happened.
  }
}

export function clearDevImpersonationSession(): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(DEV_IMPERSONATION_SESSION_KEY)
    window.dispatchEvent(new Event(DEV_IMPERSONATION_EVENT))
  } catch {
    // ignore
  }
}

export function restoreDevImpersonationSession(): boolean {
  const session = readDevImpersonationSession()
  if (!session || typeof window === 'undefined') return false
  try {
    window.localStorage.setItem(AUTH_TOKEN_KEY, session.originalToken)
    window.localStorage.setItem(AUTH_REFRESH_TOKEN_KEY, session.originalRefreshToken)
    window.sessionStorage.removeItem(DEV_IMPERSONATION_SESSION_KEY)
    window.dispatchEvent(new CustomEvent(AUTH_STATE_EVENT, {
      detail: {
        token: session.originalToken,
        refreshToken: session.originalRefreshToken,
      },
    }))
    window.dispatchEvent(new Event(DEV_IMPERSONATION_EVENT))
    return true
  } catch {
    return false
  }
}

export function subscribeDevImpersonationSession(listener: () => void): () => void {
  if (typeof window === 'undefined') return () => {}
  const wrapped = () => listener()
  window.addEventListener(DEV_IMPERSONATION_EVENT, wrapped)
  window.addEventListener('storage', wrapped)
  return () => {
    window.removeEventListener(DEV_IMPERSONATION_EVENT, wrapped)
    window.removeEventListener('storage', wrapped)
  }
}
